import { useEffect, useState } from "react";
import { Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Label } from "@/components/ui/label";

export default function AnnouncementTab() {
    const [text, setText] = useState("");
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/announcement")
            .then((r) => {
                setText(r.data.text);
                setActive(r.data.active);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/announcement", { text, active });
            toast.success(active ? "Announcement published" : "Announcement saved (hidden)");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return <Loader2 className="w-6 h-6 text-[#FF5500] animate-spin" data-testid="announcement-loading" />;

    return (
        <div data-testid="admin-announcement-tab" className="max-w-2xl">
            <div className="card-arena rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-1">
                    <Megaphone className="w-5 h-5 text-[#FFB800]" />
                    <h2 className="font-display font-bold uppercase text-xl text-white">Homepage Announcement</h2>
                </div>
                <p className="text-sm text-slate-400 mb-6">
                    Shown as a banner at the top of the public homepage while published.
                </p>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Message</Label>
                <textarea
                    data-testid="announcement-text"
                    rows={3}
                    maxLength={200}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="e.g. Finals moved to Sunday 7PM at YKP Arena"
                    className="mt-2 w-full bg-[#08080C] border border-[#262638] focus:border-[#FF5500] rounded-xl p-3 text-white text-sm outline-none transition-colors duration-200"
                />
                <p data-testid="announcement-counter" className="text-right text-[10px] font-stat text-slate-500 mt-1">
                    {text.length}/200
                </p>
                <div className="flex flex-wrap items-center justify-between gap-4 mt-5">
                    <button
                        type="button"
                        data-testid="announcement-toggle"
                        onClick={() => setActive(!active)}
                        className={`flex items-center gap-2 font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full border transition-colors duration-200 ${
                            active
                                ? "bg-[#22C55E]/15 border-[#22C55E]/50 text-[#22C55E]"
                                : "border-[#262638] text-slate-400 hover:border-[#FFB800]"
                        }`}
                    >
                        <span className={`w-2 h-2 rounded-full ${active ? "bg-[#22C55E]" : "bg-slate-600"}`} />
                        {active ? "Published" : "Hidden"}
                    </button>
                    <button
                        data-testid="announcement-save"
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest text-xs px-6 py-2.5 rounded-full transition-colors duration-200"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Save Announcement
                    </button>
                </div>
            </div>
        </div>
    );
}
